import { forwardRef, useState } from "react";
import { useTheme } from "styled-components";
import { Button } from "../Button/Button.js";
import { Flex } from "../Flex/Flex.js";
import { ModalV2 } from "../ModalV2/ModalV2.js";
import { Text } from "../Text/Text.js";
import { TextSize, TextWeight } from "../Text/Text.types.js";
import { ITheme } from "../Theme/Theme.types.js";
import { StyledBody, StyledButtons, StyledTitleSubtitleWrapper, StyledTitleWrapper } from "./ConfirmModal.style.js";
import { BaseConfirmModal, ConfirmModalProps, DefaultConfirmModalProps } from "./ConfirmModal.types.js";

const defaultProps: DefaultConfirmModalProps = {
    closeButton: false,
};

const CTAButton = ({ onClickAsync, children, ...props }: BaseConfirmModal["buttons"][number]): JSX.Element => {
    const [loading, setLoading] = useState(false);

    const handleAsyncClick = async (params: Parameters<NonNullable<BaseConfirmModal["buttons"][number]["onClickAsync"]>>) => {
        setLoading(true);
        await onClickAsync?.(...params);
        setLoading(false);
    };

    return (
        <Button width="100%" {...props} {...(onClickAsync ? { onClick: (...params) => handleAsyncClick(params), loading } : {})}>
            {children}
        </Button>
    );
};

export const ConfirmModal = forwardRef<HTMLDivElement, ConfirmModalProps>((props, ref): JSX.Element => {
    const { buttons, stylesButtons, ...attributes } = { ...defaultProps, ...props };

    const theme = useTheme() as ITheme;

    const getModalProps = () => {
        if ("content" in attributes) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { content, ...modalProps } = attributes;
            return modalProps;
        }

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { title, subtitle, ...modalProps } = attributes;
        return {
            ...modalProps,
            title: <StyledTitleWrapper>{title}</StyledTitleWrapper>,
        };
    };

    return (
        <ModalV2 ref={ref} {...getModalProps()}>
            <StyledBody>
                <Flex direction="column" gap="4">
                    {attributes.title && (
                        <Flex width="100%" justify={attributes.noCenterTitle ? "start" : "center"} align="center">
                            <Text weight={TextWeight.SEMIBOLD} size={TextSize.SMALL}>
                                {attributes.title}
                            </Text>
                        </Flex>
                    )}
                    {"content" in attributes ? (
                        <div>{attributes.content}</div>
                    ) : (
                        <StyledTitleSubtitleWrapper>
                            <Text color={theme.grayscale[80]} size={TextSize["X-SMALL"]}>
                                {attributes.subtitle}
                            </Text>
                        </StyledTitleSubtitleWrapper>
                    )}
                </Flex>
                {buttons.length > 0 && (
                    <StyledButtons style={stylesButtons}>
                        {buttons.map((buttonProps, idx) => (
                            <CTAButton key={idx} {...buttonProps} />
                        ))}
                    </StyledButtons>
                )}
            </StyledBody>
        </ModalV2>
    );
});
